class TrieNode {
  constructor() {
    this.children = new Map();
    this.isEndOfWord = false;
  }
}

class Trie {
  constructor() {
    this.root = new TrieNode();
  }

  insert(word) {
    let node = this.root;
    for(const char of word.toUpperCase()) {
      if(!node.children.has(char)) {
        node.children.set(char, new TrieNode());
      }
      node = node.children.get(char);
    }
    node.isEndOfWord = true;
  }

  getNode(word) {
    let node = this.root;
    for(const char of word) {
      if(!node.children.has(char)) return null;
      node = node.children.get(char);
    }
    return node;
  }

  isPrefix = (prefix) => !!this.getNode(prefix);
  isWord = (word) => this.getNode(word)?.isEndOfWord || false;
}

const DIRECTIONS = [
  [-1, -1], [-1, 0], [-1, 1],
  [0, -1],           [0, 1],
  [1, -1],  [1, 0],  [1, 1]
];

const buildTrie = (dictionary) => {
  const trie = new Trie();
  dictionary.forEach(word => word && trie.insert(word));
  return trie;
};

const getNeighbors = (row, col, size = 4) => 
  DIRECTIONS
    .map(([dr, dc]) => [row + dr, col + dc])
    .filter(([r, c]) => r >= 0 && r < size && c >= 0 && c < size);

const dfs = (board, row, col, node, path, visited, solutions, currentWord = '') => {
  const char = board[row][col];
  const word = currentWord + char;
  const posKey = `${row}-${col}`;
  
  visited.add(posKey);
  path.push({ row, col });
  
  const nextNode = node.children.get(char);
  if(!nextNode) {
    visited.delete(posKey);
    path.pop();
    return;
  }

  // Found a valid word
  if(nextNode.isEndOfWord && word.length >= 3) {
    solutions[word] = [...path];
  }

  // Continue searching
  for(const [nextRow, nextCol] of getNeighbors(row, col)) {
    const nextPosKey = `${nextRow}-${nextCol}`;
    const nextChar = board[nextRow][nextCol];
    
    if(!visited.has(nextPosKey) && nextNode.children.has(nextChar)) {
      dfs(board, nextRow, nextCol, nextNode, path, visited, solutions, word);
    }
  }
  
  visited.delete(posKey);
  path.pop();
};

export const findWords = (board, dictionary) => {
  if(!dictionary?.length) return [];

  const trie = buildTrie(dictionary);
  const solutions = {};

  // Start DFS from each cell
  for(let row = 0; row < 4; row++) {
    for(let col = 0; col < 4; col++) {
      const char = board[row][col];
      if(trie.root.children.has(char)) {
        dfs(board, row, col, trie.root, [], new Set(), solutions);
      }
    }
  }

  // Convert to array and sort by length (desc) then alphabetically
  return Object.entries(solutions)
    .map(([word, path]) => ({ word, path }))
    .sort((a, b) => b.word.length - a.word.length || a.word.localeCompare(b.word));
};

const findWordPath = (board, word, startRow, startCol, index = 1, path = [], visited = new Set()) => {
  if(index === word.length) return [...path];

  for(const [row, col] of getNeighbors(startRow, startCol)) {
    const posKey = `${row}-${col}`;
    
    if(!visited.has(posKey) && board[row][col] === word[index]) {
      visited.add(posKey);
      path.push({ row, col });
      
      const result = findWordPath(board, word, row, col, index + 1, path, visited);
      if(result) return result;
      
      visited.delete(posKey);
      path.pop();
    }
  }
  return null;
};

export const validateWordPath = (board, word) => {
  const upperWord = word.toUpperCase();
  
  for(let row = 0; row < 4; row++) {
    for(let col = 0; col < 4; col++) {
      if(board[row][col] === upperWord[0]) {
        const visited = new Set([`${row}-${col}`]);
        const path = [{ row, col }];
        
        const result = findWordPath(board, upperWord, row, col, 1, path, visited);
        if(result) return result;
      }
    }
  }
  return null;
};

export { Trie, TrieNode, buildTrie };